import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Camera, Upload, Trash2, Edit3, X } from 'lucide-react';
import { confirmAction } from '@/lib/confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function ScreenshotsPage() {
  const { isAdmin } = useAuth();
  const [screenshots, setScreenshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editSS, setEditSS] = useState(null);
  const [editCaption, setEditCaption] = useState('');
  const [viewSS, setViewSS] = useState(null);

  useEffect(() => {
    fetchScreenshots();
  }, []);

  const fetchScreenshots = async () => {
    try {
      const { data } = await axios.get(`${API}/screenshots`);
      setScreenshots(data);
    } catch (err) {
      console.error('Failed to fetch screenshots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('caption', uploadCaption);
      await axios.post(`${API}/admin/screenshots`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Screenshot uploaded!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadCaption('');
      fetchScreenshots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmAction('Delete this screenshot?', { confirmLabel: 'Delete' }))) return;
    try {
      await axios.delete(`${API}/admin/screenshots/${id}`);
      toast.success('Screenshot deleted');
      fetchScreenshots();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEditCaption = async (e) => {
    e.preventDefault();
    if (!editSS) return;
    try {
      await axios.put(`${API}/admin/screenshots/${editSS.id}`, { caption: editCaption });
      toast.success('Caption updated');
      setEditSS(null);
      fetchScreenshots();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="screenshots-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-2">Gallery</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-white">
              Screen Shots
            </h1>
          </div>
          {isAdmin && (
            <Button
              onClick={() => setUploadOpen(true)}
              className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-10 px-6"
              data-testid="upload-screenshot-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : screenshots.length === 0 ? (
          <div className="text-center py-24 bg-[#121212] border border-zinc-800" data-testid="no-screenshots">
            <Camera className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-white mb-2">No Screenshots Yet</h2>
            <p className="text-zinc-500">Check back later for game screenshots and highlights.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {screenshots.map((ss) => (
              <div key={ss.id} className="bg-[#121212] border border-zinc-800 overflow-hidden group" data-testid={`screenshot-${ss.id}`}>
                <div className="relative cursor-pointer" onClick={() => setViewSS(ss)}>
                  <img
                    src={`${BASE}${ss.image_url}`}
                    alt={ss.caption || 'Screenshot'}
                    className="w-full aspect-video object-cover"
                  />
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditSS(ss); setEditCaption(ss.caption || ''); }}
                        className="w-7 h-7 bg-cyan-500/80 text-white flex items-center justify-center"
                        data-testid={`edit-ss-${ss.id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(ss.id); }}
                        className="w-7 h-7 bg-red-500/80 text-white flex items-center justify-center"
                        data-testid={`delete-ss-${ss.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                {ss.caption && (
                  <div className="p-4">
                    <p className="text-zinc-300 text-sm">{ss.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="upload-ss-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Upload Screenshot</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Image</Label>
              <label className="block border border-dashed border-zinc-700 p-8 text-center cursor-pointer hover:border-zinc-500 transition-colors">
                <input type="file" accept="image/*" className="hidden" data-testid="ss-file-input" onChange={(e) => setUploadFile(e.target.files[0])} />
                {uploadFile ? (
                  <span className="text-cyan-400 text-sm">{uploadFile.name}</span>
                ) : (
                  <span className="text-zinc-600 text-sm flex flex-col items-center gap-2">
                    <Camera className="w-8 h-8" />
                    Click to select image
                  </span>
                )}
              </label>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm uppercase tracking-wide">Caption (optional)</Label>
              <Textarea
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
                placeholder="Add a description..."
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none"
                data-testid="ss-caption-input"
              />
            </div>
            <Button type="submit" disabled={uploading || !uploadFile} className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="ss-upload-submit">
              {uploading ? 'Uploading...' : 'Upload Screenshot'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Caption Dialog */}
      <Dialog open={!!editSS} onOpenChange={() => setEditSS(null)}>
        <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="edit-ss-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white uppercase">Edit Caption</DialogTitle>
          </DialogHeader>
          {editSS && (
            <form onSubmit={handleEditCaption} className="space-y-4 mt-4">
              <Textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                placeholder="Add a description..."
                className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none min-h-[80px] focus:border-cyan-400 resize-none"
                data-testid="edit-ss-caption"
              />
              <Button type="submit" className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="save-ss-caption">
                Save Caption
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Fullscreen View Dialog */}
      <Dialog open={!!viewSS} onOpenChange={() => setViewSS(null)}>
        <DialogContent className="bg-black border-zinc-800 rounded-none max-w-4xl p-0" data-testid="view-ss-dialog">
          {viewSS && (
            <div>
              <img src={`${BASE}${viewSS.image_url}`} alt={viewSS.caption || 'Screenshot'} className="w-full" />
              {viewSS.caption && (
                <div className="p-4 bg-[#121212]">
                  <p className="text-zinc-300 text-sm">{viewSS.caption}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
